import { formatDate } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbCalendar, NgbDate, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CreateEventService } from 'src/app/services/create-event.service';
import { TicketsService } from 'src/app/services/tickets.service';

@Component({
  selector: 'app-sale-config-create',
  templateUrl: './sale-config-create.component.html',
  styleUrls: ['./sale-config-create.component.scss']
})
export class SaleConfigCreateComponent {

  @Input() idEvent!: any;
  @Input() configTickets!: any;
  // Edición de un evento ya existente (no el alta inicial, donde el borrador ya
  // tiene id pero todavía no puede tener pedidos). Bloquea las entradas
  // nominativas, igual que el tipo de evento en RouterCreateComponent.
  @Input() embedded: boolean = false;
  @Output() changeTab: EventEmitter<any> = new EventEmitter<any>();

  // Config
  formConfig!: FormGroup;
  typeDatepicker: string | null = null;

  // Start and End Date
  startStringDate!: any;
  endStringDate!: any;
  startDate!: NgbDate;
  endDate!: NgbDate;

  hourInit!: any;
  minuteInit!: any;
  hourEnd!: any;
  minuteEnd!: any;

  // String versions for inputs
  hourInitStr: string = '';
  minuteInitStr: string = '';
  hourEndStr: string = '';
  minuteEndStr: string = '';

  // Dates
  datesInfo!: any;
  hoursInfo!: any;
  dateOld!: NgbDate;
  minDate!: NgbDate;

  sessions: any[] = [];
  selectedSession: any = 0;
  typeTicketsSession: boolean = false;

  // Tope por usuario que había antes de activar las entradas nominativas, para
  // poder devolverlo si se desactivan (ver syncNominativeLimit). `undefined`
  // significa "no hay nada guardado"; null y '' son valores válidos del campo
  // (estaba vacío) y hay que poder restaurarlos.
  private maxTicketsBeforeNominative: any = undefined;

  // El control se deshabilita en edición, y los controles deshabilitados no
  // aparecen en formConfig.value: hay que leerlo del valor bruto.
  get isNominative(): boolean {
    return !!this.formConfig?.getRawValue().nominative_tickets;
  }

  @ViewChild('startDateField') startDateField!: ElementRef;
  @ViewChild('endDateField') endDateField!: ElementRef;

  constructor(
    private ticketService: TicketsService,
    private router: Router,
    public formatter: NgbDateParserFormatter,
    private activatedRoute: ActivatedRoute,
    private createService: CreateEventService,
    private formBuilder: FormBuilder,
    private calendar: NgbCalendar
  ) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.typeDatepicker) {
      return;
    }
    const target = event.target as Node;
    const insideStart = this.startDateField?.nativeElement.contains(target);
    const insideEnd = this.endDateField?.nativeElement.contains(target);
    if (!insideStart && !insideEnd) {
      this.typeDatepicker = null;
    }
  }

  ngOnInit() {
    // Inicializar fecha mínima como hoy
    this.minDate = this.calendar.getToday();
    this.dateOld = this.calendar.getToday();

    let saved = this.createService.getInfoEvent();

    this.sessions = saved?.values?.sessions || [];

    this.formConfig = this.formBuilder.group({
      start_sales: [],
      end_sales: [],
      publish_date: [],
      assume_fees: [true],
      need_validation: [false],
      publish: [false],
      max_tickets_per_user: [],
      nominative_tickets: [false]
    });

    // En edición el evento ya puede tener pedidos hechos, así que las entradas
    // nominativas quedan fijadas. La plantilla ya no pinta el interruptor en ese
    // caso; deshabilitar el control además cierra la puerta a que un cambio
    // futuro en la plantilla lo vuelva editable sin darse cuenta. El valor sigue
    // viajando en el payload porque abajo se lee con getRawValue().
    if (this.embedded) {
      this.formConfig.controls['nominative_tickets'].disable({ emitEvent: false });
    }

    this.formConfig.valueChanges.subscribe(() => {
      // Antes de propagar: con entradas nominativas el tope por usuario es 1.
      this.syncNominativeLimit();

      const value = {
        ...this.formConfig.getRawValue(),
        typeTicketsSession: this.typeTicketsSession,
        sessions: this.sessions
      };
      this.editConfig(value);
    });

    let infoEvent = this.createService.getInfoEvent();
    if (infoEvent) {
      this.datesInfo = infoEvent.values?.infoDates;
      this.hoursInfo = infoEvent.values?.infoHours;
    }

    let config = this.ticketService.getTicketsConfig();
    if (config) {
      // Copia superficial: config es la misma referencia que guarda el observable
      // compartido (comparada tal cual contra el snapshot original en
      // RouterCreateComponent.hasUnsavedChanges). Mutar sus fechas de string a Date
      // in-place haría que esa comparación fallara solo por abrir esta pestaña.
      this.configTickets = { ...config };
      if (this.configTickets?.start_sales) {
        if (typeof (this.configTickets.start_sales) == 'string') {
          this.configTickets.start_sales = new Date(this.configTickets.start_sales);
        }
        if (typeof (this.configTickets.end_sales) == 'string') {
          this.configTickets.end_sales = new Date(this.configTickets.end_sales);
        }
        this.startStringDate = formatDate(this.configTickets.start_sales, 'dd/MM/yyyy', 'es');
        this.hourInit = this.configTickets.start_sales.getHours();
        this.minuteInit = this.configTickets.start_sales.getMinutes();
        this.hourInitStr = this.hourInit.toString().padStart(2, '0');
        this.minuteInitStr = this.minuteInit.toString().padStart(2, '0');
      }
      if (this.configTickets?.end_sales) {
        this.endStringDate = formatDate(this.configTickets.end_sales, 'dd/MM/yyyy', 'es');
        this.hourEnd = this.configTickets.end_sales.getHours();
        this.minuteEnd = this.configTickets.end_sales.getMinutes();
        this.hourEndStr = this.hourEnd.toString().padStart(2, '0');
        this.minuteEndStr = this.minuteEnd.toString().padStart(2, '0');
      }
      // emitEvent: false para no disparar el valueChanges de abajo, que escribiría
      // de vuelta en el servicio con campos añadidos (typeTicketsSession, sessions)
      // y así generaría un falso "cambios sin guardar" solo por abrir la pestaña.
      this.formConfig.patchValue(this.configTickets, { emitEvent: false });
      // Un evento que ya era nominativo tiene que abrirse con el 1 puesto: el
      // patchValue de arriba no dispara el valueChanges que lo haría.
      this.syncNominativeLimit();
    }
  }

  /**
   * Mantiene el tope de entradas por usuario a 1 mientras las entradas sean
   * nominativas.
   *
   * El límite real lo impone el backend a partir del flag del evento
   * (PurchaseLimitService::effectiveLimit), no de este campo; aquí solo se
   * refleja para que el gestor vea sin ambigüedad cuál va a ser el tope.
   *
   * Al desactivarlas se devuelve el valor que hubiera antes, para no dejar un 1
   * impuesto por una opción que ya no está activa.
   */
  private syncNominativeLimit(): void {
    const control = this.formConfig.controls['max_tickets_per_user'];

    if (this.isNominative) {
      if (+control.value === 1) {
        return;
      }
      this.maxTicketsBeforeNominative = control.value;
      // emitEvent: false para no reentrar en el valueChanges que llama aquí.
      control.setValue(1, { emitEvent: false });
      return;
    }

    if (this.maxTicketsBeforeNominative !== undefined) {
      control.setValue(this.maxTicketsBeforeNominative, { emitEvent: false });
      this.maxTicketsBeforeNominative = undefined;
    }
  }

  saveDate(event: any) {
    if (event) {
      const date = new Date(this.formatter.format(event));
      date.setHours(0);
      date.setMinutes(0);
      if (this.typeDatepicker == 'start') {
        this.startDate = event;
        this.formConfig.controls['start_sales'].setValue(date);
        this.startStringDate = formatDate(date, 'dd/MM/yyyy', 'es');
      }
      if (this.typeDatepicker == 'end') {
        this.endDate = event;
        this.formConfig.controls['end_sales'].setValue(date);
        this.endStringDate = formatDate(date, 'dd/MM/yyyy', 'es');
      }
    }
    this.typeDatepicker = null;
    this.saveConfig();
  }

  saveConfig() {
    this.applyTime('start_sales', this.hourInit, this.minuteInit);
    this.applyTime('end_sales', this.hourEnd, this.minuteEnd);
  }

  /**
   * Vuelca la hora tecleada a mano sobre la fecha ya elegida en el datepicker.
   *
   * Tiene que hacerlo con setValue y no mutando el Date en su sitio: lo que se
   * guarda al final es lo que hay en el servicio (ver editConfig), y el servicio
   * solo se refresca desde el valueChanges del formulario. Mutando el Date no
   * saltaba ese valueChanges, así que un cambio de solo la hora se perdía al
   * guardar y aparentaba guardarse únicamente si además se tocaba la fecha
   * (entonces sí había setValue, y la mutación colaba de rebote porque el
   * servicio se quedaba con la misma referencia de Date).
   */
  private applyTime(control: 'start_sales' | 'end_sales', hour: any, minute: any): void {
    // hour puede ser 0 (medianoche): comparar contra null, nunca por truthiness.
    if (hour == null || minute == null) {
      return;
    }

    const current = this.formConfig.controls[control].value;
    if (!current) {
      return;
    }

    const base = current instanceof Date ? current : new Date(current);
    if (isNaN(base.getTime())) {
      return;
    }

    const date = new Date(base.getTime());
    date.setHours(hour, minute);
    if (date.getTime() === base.getTime()) {
      return;
    }

    // Antes del setValue: el payload que emite el valueChanges arrastra sessions.
    if (this.sessions[this.selectedSession]) {
      this.sessions[this.selectedSession][control] = date;
    }
    this.formConfig.controls[control].setValue(date);
  }

  openDatePicker(type: string) {
    if (this.typeDatepicker) {
      this.typeDatepicker = null;
      return;
    }
    this.typeDatepicker = type;
  }


  editConfig(payload: any) {
    this.ticketService.setTicketsConfig(payload);
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  validateHourInput(event: any, type: 'hourInit' | 'hourEnd') {
    const input = event.target;
    let value = input.value.replace(/[^0-9]/g, ''); // Solo números

    if (value.length > 2) {
      value = value.slice(0, 2);
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 23) {
      value = '23';
    }

    // Formatear con cero a la izquierda si tiene valor
    if (value && value.length > 0) {
      value = value.padStart(2, '0');
    }

    if (type === 'hourInit') {
      this.hourInitStr = value;
      this.hourInit = value ? parseInt(value, 10) : null;
    } else {
      this.hourEndStr = value;
      this.hourEnd = value ? parseInt(value, 10) : null;
    }
    this.saveConfig();
  }

  validateMinuteInput(event: any, type: 'minuteInit' | 'minuteEnd') {
    const input = event.target;
    let value = input.value.replace(/[^0-9]/g, ''); // Solo números

    if (value.length > 2) {
      value = value.slice(0, 2);
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 59) {
      value = '59';
    }

    // Formatear con cero a la izquierda si tiene valor
    if (value && value.length > 0) {
      value = value.padStart(2, '0');
    }

    if (type === 'minuteInit') {
      this.minuteInitStr = value;
      this.minuteInit = value ? parseInt(value, 10) : null;
    } else {
      this.minuteEndStr = value;
      this.minuteEnd = value ? parseInt(value, 10) : null;
    }
    this.saveConfig();
  }

  changeSession(event: any) {
    if (this.sessions[this.selectedSession]) {
      this.sessions[this.selectedSession].max_tickets_per_user = this.formConfig.value.max_tickets_per_user;
    }

    this.selectedSession = event;

    if (this.sessions[event]) {
      this.formConfig.patchValue(this.sessions[event]);
    }

    this.startStringDate = null;
    this.endStringDate = null;
    this.hourInit = null;
    this.minuteInit = null;
    this.hourEnd = null;
    this.minuteEnd = null;

    if (this.sessions[event]?.start_sales) {
      this.startStringDate = formatDate(this.sessions[event].start_sales, 'dd/MM/yyyy', 'es');
      this.hourInit = this.sessions[event].start_sales.getHours();
      this.minuteInit = this.sessions[event].start_sales.getMinutes();
    }
    if (this.sessions[event]?.end_sales) {
      this.endStringDate = formatDate(this.sessions[event].end_sales, 'dd/MM/yyyy', 'es');
      this.hourEnd = this.sessions[event].end_sales.getHours();
      this.minuteEnd = this.sessions[event].end_sales.getMinutes();
    }
  }

  changeTypeTicketsSession(event: any) {
    if (event) {
      this.selectedSession = null;
    } else {
      this.selectedSession = 0;
    }
  }

  nextStep() {
    // Guardar configuración antes de ir al siguiente paso
    this.saveConfig();
  }
}
